import { Box, FormHelperText, Grid, Link, MenuItem, Typography, styled } from '@mui/material';
import { SectionStyled } from 'containers/FormStep1Container';
import { t } from 'i18next';
import { palette } from 'styles/theme';
import ErrorIcon from '@mui/icons-material/Error';
import SelectInput from 'components/SelectInput';
import { Controller, useFormContext } from 'react-hook-form';
import { ManagementsType } from 'containers/FormStep3Section1Container';
import { useEffect, useState } from 'react';
import VideoCallService, { VideoCallSchedule, VideoCallScheduleBook } from 'services/VideoCallService';
import { format } from 'date-fns';
import { hasDuplicates } from 'helpers/utils/object';
import LoadingButton from '@mui/lab/LoadingButton';

const ManagementPage = () => {
  const { getValues, control, trigger, setValue } = useFormContext();
  const managements: ManagementsType[] = getValues('entity.managements');
  const submissionId = getValues('submissionId');
  const [videoScheduleList, setVideoScheduleList] = useState<VideoCallSchedule[]>([]);
  const [modifiedVideoScheduleList, setModifiedVideoScheduleList] = useState<VideoCallSchedule[]>([]);
  const [videoScheduleBooked, setVideoScheduleBooked] = useState<VideoCallScheduleBook[]>([]);
  const videoCallBookingCompleted = videoScheduleBooked?.length === managements.length;
  const [isLoadingVideoCallBooking, setIsLoadingVideoCallBooking] = useState(false);

  useEffect(() => {
    (async () => {
      VideoCallService.getVideoCallSchedule({ id: submissionId }).then((res) => {
        setModifiedVideoScheduleList(res.data);
        return setVideoScheduleList(res.data);
      });
    })();
  }, [setValue, submissionId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (getValues('videoCallSchedule') || getValues('videoCallSchedule')?.length === 0) {
      setValue('videoCallSchedule', Array(managements.length).fill(''));
    }
  }, [getValues, managements.length, setValue]);

  const formatDateTimeVideoSchedule = (videoSchedule: VideoCallSchedule | undefined) => {
    if (!videoSchedule) return '';
    const startAt = new Date(videoSchedule.start_at);
    const endAt = new Date(videoSchedule.end_at);

    return `${format(startAt, 'dd/MM/yyyy HH:mm')} - ${format(endAt, 'HH:mm')} WIB`;
  };

  const handleOnClickBookVideoCallSchedule = async () => {
    try {
      setIsLoadingVideoCallBooking(true);
      const videoCall = await VideoCallService.bookVideoCallSchedule({
        id: submissionId,
        data: getValues('videoCallSchedule').map((videoCallScheduleId: string) => ({
          video_call_schedule_id: videoCallScheduleId
        }))
      });
      setVideoScheduleBooked(videoCall.data);
      setValue('videoCallBooked', videoCall.data);
    } catch (error) {
      console.log(error);
    } finally {
      trigger('videoCallBooked');
      setIsLoadingVideoCallBooking(false);
    }
  };

  const handleOnChangeBookVideoCallSchedule = () => {
    trigger('videoCallSchedule');

    // exclude id from the modified list if has been selected
    setModifiedVideoScheduleList(
      videoScheduleList.filter((videoSchedule) => !getValues('videoCallSchedule').includes(videoSchedule.id))
    );
  };

  return (
    <>
      <Typography color="surface.700" variant="h6" sx={{ mb: 1 }}>
        {t('step4.title')}
      </Typography>
      <Box sx={{ mb: 1 }}>
        <Typography color="surface.900" fontWeight="500" variant="body2">
          {t('step4.desc')}
        </Typography>
      </Box>
      <Box sx={{ my: 1 }}>
        <Typography color="surface.700" variant="h7">
          Pengurus
        </Typography>
      </Box>
      <SectionStyled sx={{ backgroundColor: palette.surface[100] }}>
        {/* form name */}
        {managements.map((management, index) => (
          <Grid key={index} container sx={{ mb: 3, width: 'inherit' }}>
            <Grid item md={4}>
              <Typography sx={{ paddingTop: 2, color: 'surface.400', fontSize: '12px', lineHeight: '18px' }}>
                Name
              </Typography>
              <Typography fontWeight={600} fontSize={12}>
                {management.name}
              </Typography>
              <Typography sx={{ paddingTop: 2, color: 'surface.400', fontSize: '12px', lineHeight: '18px' }}>
                Alamat Email
              </Typography>
              <Typography fontWeight={600} fontSize={12}>
                {management.email_address}
              </Typography>
            </Grid>
            <Grid item md={4}>
              <Typography sx={{ paddingTop: 2, color: 'surface.400', fontSize: '12px', lineHeight: '18px' }}>
                Nomor Identitas
              </Typography>
              <Typography fontWeight={600} fontSize={12}>
                {management.identity_number}
              </Typography>
              <Typography sx={{ paddingTop: 2, color: 'surface.400', fontSize: '12px', lineHeight: '18px' }}>
                Nomor Handphone
              </Typography>
              <Typography fontWeight={600} fontSize={12}>
                {management.phone_number}
              </Typography>
            </Grid>
            {!!videoScheduleList.length && (
              <Grid item md={4} sx={{ bgcolor: palette.surface[50], p: 2 }}>
                <Controller
                  name={`videoCallSchedule[${index}]` as string}
                  control={control}
                  rules={{
                    required: 'Pilih Jadwal Video Call',
                    validate: {
                      hasDuplicate: (value, formValues) =>
                        !hasDuplicates(formValues.videoCallSchedule) ||
                        'Jadwal Video Call tidak bisa pada saat yang sama'
                    }
                  }}
                  render={({ field: hookForm }) => (
                    <>
                      <SelectInput
                        name={`videoCallSchedule[${index}]`}
                        placeholder="Pilih"
                        defaultValue={''}
                        label="Pilih Jadwal Video Call"
                        fullWidth
                        disabled={videoCallBookingCompleted}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <SpanStyled>{'Pilih'}</SpanStyled>;
                          }
                          return formatDateTimeVideoSchedule(videoScheduleList.find((video) => video.id === selected));
                        }}
                        onChange={handleOnChangeBookVideoCallSchedule}
                        hookForm={hookForm}
                      >
                        {modifiedVideoScheduleList.map((schedule) => (
                          <MenuItem key={schedule.id} value={schedule.id}>
                            {formatDateTimeVideoSchedule(schedule)}
                          </MenuItem>
                        ))}
                      </SelectInput>
                      {videoScheduleBooked[index] && (
                        <Box>
                          <Typography variant="body1" sx={{ color: 'surface.300', fontSize: 12, fontWeight: 400 }}>
                            Link Video Call
                          </Typography>
                          <Link href={videoScheduleBooked[index].url} underline="hover" target="_blank" rel="noopener">
                            {videoScheduleBooked[index].url}
                          </Link>
                        </Box>
                      )}
                    </>
                  )}
                />
              </Grid>
            )}
          </Grid>
        ))}
        <Grid container sx={{ flexDirection: 'row-reverse' }}>
          <Grid item md={4}>
            <Controller
              name="videoCallBooked"
              control={control}
              rules={{
                validate: {
                  hasNotBooked: (value, formValues) => formValues.videoCallBooked?.length || 'Harus konfirmasi jadwal'
                }
              }}
              render={({ formState }) => (
                <>
                  <FormHelperText sx={{ m: 0 }} error={Boolean(formState.errors.videoCallBooked?.message)}>
                    {formState.errors.videoCallBooked?.message as string}
                  </FormHelperText>
                  <LoadingButton
                    color="primary"
                    variant="contained"
                    loading={isLoadingVideoCallBooking}
                    disabled={videoScheduleList.length === 0 || videoCallBookingCompleted}
                    sx={{
                      width: '100%',
                      p: 1,
                      fontSize: '14px',
                      lineHeight: '24px'
                    }}
                    onClick={handleOnClickBookVideoCallSchedule}
                  >
                    Konfirmasi Jadwal
                  </LoadingButton>
                </>
              )}
            />
          </Grid>
        </Grid>
      </SectionStyled>
      <Box sx={{ my: 3 }}>
        <AttentionText variant="body1">
          <>
            <ErrorIcon sx={{ fontSize: 20, color: 'error.main', marginBottom: -0.5, mx: 3 }} />
            Pastikan ketersediaan jadwal masing masing pengurus sesuai dengan jadwal yang dikonfirmasi
          </>
        </AttentionText>
      </Box>
    </>
  );
};

export default ManagementPage;

const SpanStyled = styled('span')(({ theme }) => ({
  color: theme.palette.surface[300]
}));

const AttentionText = styled(Typography)(() => ({
  fontWeight: 600,
  backgroundColor: '#FFEFC2',
  padding: 16,
  borderRadius: 4,
  textAlign: 'center'
}));
